import { Link } from "react-router-dom";
import { GitCompareArrows, Layers } from "lucide-react";
import { PageHeader } from "@/components/molecules/PageHeader";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { RunStatusBadge } from "@/components/organisms/studio/RunStatusBadge";
import { useEngineAvailable, useEnginePlans } from "@/hooks/migrations/engine";
import { familyLabel } from "@/lib/migrations/families";
import { formatRelative } from "@/lib/studio/utils";
import { ENGINE_PLAN_SCOPE_ORG } from "@/services/migrations/engine";
import type { EnginePlanSummary } from "@/services/migrations/engine";

/**
 * Migration-engine plan index.
 *
 * GATED ON A RUNTIME PROBE. The engine API does not exist in every deployment; until it
 * answers, this page renders an explanation and nothing else, and no other migrations
 * screen is touched. Nothing here shares state, routes or components with the playbook
 * path beyond the presentational atoms both already used.
 *
 * THIS LIST CARRIES NO COVERAGE, AND SAYS SO RATHER THAN INVENTING ONE. `GET /plans`
 * returns ten fields per plan and not one of them is a count — no `undeclared_total`, no
 * validation tallies. An earlier version of this page rendered an "undeclared" chip here
 * anyway; against the real endpoint that chip would have printed "undeclared not
 * reported" on every row forever, which is technically true and useless, or (worse, had
 * the fallback been zero) a clean verdict the endpoint never gave. The verdict lives in
 * the report, one click away, and the row says which plans are worth opening by status.
 */
export function EnginePlans() {
  const available = useEngineAvailable();
  const plansQ = useEnginePlans({ limit: 50 });
  const plans = plansQ.data?.plans ?? [];

  return (
    <div className="flex h-full flex-col gap-4 animate-fade-in bg-background">
      <PageHeader
        icon={GitCompareArrows}
        title="Migration Engine"
        description="Deterministic metadata migrations — coverage, residue and the manual steps that remain"
        actions={
          // Outside the engine's availability probe on purpose. The portfolio inventory
          // is a different, free endpoint, and an org with no engine deployed can still
          // be shown its own scale — which is the whole point of that page.
          <Button variant="ghost" size="sm" asChild>
            <Link to="/migration-portfolio">
              <Layers className="mr-1 h-3.5 w-3.5" />
              Portfolio pass
            </Link>
          </Button>
        }
      />

      <div className="flex-1 min-h-0 overflow-y-auto">
        {available.isLoading ? (
          <p className="font-mono text-xs text-muted-foreground">Checking for the engine…</p>
        ) : available.data !== true ? (
          <EngineUnavailable />
        ) : plansQ.isLoading ? (
          <p className="font-mono text-xs text-muted-foreground">Loading plans…</p>
        ) : plans.length === 0 ? (
          <p className="font-mono text-xs text-muted-foreground">
            No migration plans yet for this org.
          </p>
        ) : (
          <ul className="space-y-2">
            {plans.map((plan) => (
              <li key={plan.plan_id}>
                <PlanRow plan={plan} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * One plan row.
 *
 * THE SCOPE IS READ, NOT GUESSED FROM THE TITLE. An org-scoped plan is a PORTFOLIO PASS —
 * several hundred check-only deploys of the customer's own serialised queue — and it
 * differs from a single-component plan in cost by three orders of magnitude and in which
 * routes will answer about it: `/plans/{id}/run` refuses one with a 409 naming the
 * portfolio route, and `/plans/{id}/report` is entitlement-gated for one. `_wire_plan`
 * emits `scope` precisely so a UI no longer has to match on the title string, which is
 * operator-supplied and defaults to a sentence in English.
 *
 * So an org-scoped row links to the portfolio surface, which is the surface that can
 * actually run and read it. Sending it to the component detail view would land a reader on
 * a 403 or a 404 for a plan they created themselves.
 */
function PlanRow({ plan }: { plan: EnginePlanSummary }) {
  const portfolio = plan.scope === ENGINE_PLAN_SCOPE_ORG;
  return (
    <Link
      to={portfolio ? "/migration-portfolio" : `/migration-engine/${encodeURIComponent(plan.plan_id)}`}
      className="block rounded-xl border border-border bg-card p-4 transition-colors hover:bg-muted/20"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <RunStatusBadge status={plan.status} />
          <span className="truncate font-mono text-sm text-foreground">
            {plan.title || familyLabel(plan.family) || plan.plan_id}
          </span>
          {portfolio ? (
            <Badge
              variant="warm"
              title="An org-wide portfolio pass, not a single component. It runs and reports through the portfolio surface."
            >
              portfolio pass
            </Badge>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {/* Cancellation is cooperative and durable — a plan can carry the request
              while a task already running finishes what it is doing. */}
          {plan.cancel_requested ? <Badge variant="warm">cancellation requested</Badge> : null}
          <span className="font-mono text-[11px] text-muted-foreground">
            coverage in the report
          </span>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-muted-foreground">
        <span>{familyLabel(plan.family)}</span>
        {plan.updated_at ? <span>updated {formatRelative(plan.updated_at)}</span> : null}
        {plan.engine_version ? <span>engine {plan.engine_version}</span> : null}
      </div>
      {portfolio ? (
        <p className="mt-1 max-w-prose text-[11px] text-muted-foreground">
          This is an org-wide pass over every addressable rule. It does not run through the
          single-component routes and its triage is a paid capability — the portfolio surface
          is where it lives.
        </p>
      ) : null}
    </Link>
  );
}

/**
 * The engine is not deployed here. Said plainly, once — not as an error toast and not
 * as an empty list that looks like "you have no migrations".
 */
export function EngineUnavailable() {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <p className="font-mono text-sm text-foreground">The migration engine is not available.</p>
      <p className="mt-1 max-w-prose text-xs text-muted-foreground">
        This environment has no migration-engine API. Nothing is broken and no migration
        data is missing — the surface simply has nothing to talk to. The playbook
        migrations under <span className="font-mono">/migrations</span> are unaffected.
      </p>
    </div>
  );
}

export default EnginePlans;
